import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { setSearchLiveRanges, setActiveTab, fetchDoctorsLive } from './pace-actions';
import { getActiveTab, getDoctorsTotal, getIsLoading, getLiveRangesSearch } from './pace-selectors';

import BaseGrid from '../components/grid/BaseGrid';
import SearchTemplateViewer from '../components/header/search/SearchTemplateViewer';
import { setSortCriterias } from '../components/templates/templates-actions';
import { getWeekRangeValues } from '../common/helpers/date';
import { get, first } from 'lodash';

function TemplateViewerReadOnly() {
  const dispatch = useDispatch();
  const location = useLocation<{ previousSearch?: any }>();
  const activeTab = useSelector(getActiveTab);
  const total = useSelector(getDoctorsTotal);
  const liveRangesSearch = useSelector(getLiveRangesSearch);
  const loading = useSelector(getIsLoading);
  const [showException, setShowException] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Initialize and fetch data on mount
  useEffect(() => {
    if (!mounted) {
      dispatch(setActiveTab('liveRanges'));

      // Restore previous search state if available, otherwise use default
      const previousSearch = location.state?.previousSearch;
      const initialSearch = previousSearch || {
        page: 0,
        size: 15,
        base: false,
        path: 'mapped-range0',
        search: false,
        startDate: get(first(getWeekRangeValues()), 'value'),
        employeeName: undefined,
        employeeId: undefined,
        office: undefined,
        officeName: undefined,
        regionIds: [],
        districtIds: [],
        odShiftLengthFrom: undefined,
        odShiftLengthTo: undefined,
        officeLengthHOOFrom: undefined,
        officeLengthHOOTo: undefined,
      };

      // Set search state
      dispatch(setSearchLiveRanges(initialSearch));

      // Fetch data immediately with the initial search
      dispatch(fetchDoctorsLive({ ...initialSearch, hideException: false }));
      dispatch(setSortCriterias({}));

      setMounted(true);
    }
  }, [mounted, dispatch]);

  // Fetch data when search changes (after mount)
  useEffect(() => {
    if (mounted && liveRangesSearch && liveRangesSearch.page !== undefined) {
      // Only fetch if the search was explicitly triggered (e.g., from dropdown change with valid data)
      const reasonSearch = {
        ...liveRangesSearch,
        base: false,
        path: 'mapped-range0',
        // Ensure arrays are always arrays, not undefined
        regionIds: liveRangesSearch.regionIds || [],
        districtIds: liveRangesSearch.districtIds || [],
      };
      dispatch(fetchDoctorsLive({ ...reasonSearch, hideException: showException }));
      dispatch(setSortCriterias({}));
    }
    // eslint-disable-next-line
  }, [liveRangesSearch, showException, mounted, dispatch]);

  const onPageChange = (searchData) => {
    dispatch(setSearchLiveRanges(searchData));
  };

  return (
    <>
      <SearchTemplateViewer search={liveRangesSearch} />

      <TabContainer>
        <BaseGrid
          id='liveRanges'
          activeTab={activeTab}
          total={total}
          search={liveRangesSearch}
          setSearch={(searchData) => onPageChange(searchData)}
          loading={loading}
          showException={showException}
          setShowException={setShowException}
          readOnly={true}
        />
      </TabContainer>
    </>
  );
}

export default TemplateViewerReadOnly;

const TabContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;
`;
